import { type CompanyIconKey, companyIconKeys } from '@pcrm/shared/icons';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiDelete, apiFileUrl, apiGet, apiPatch, apiPost, apiPut, apiUpload } from '../../lib/api';

/**
 * Firmele grupului, dinspre client.
 *
 * Schemele Zod sunt declarate aici, nu importate din `@pcrm/be/companies`: `apps/crm` e
 * `scope:fe` și n-are voie să depindă de backend. Aceeași alegere ca la
 * `features/settings/settings.api.ts` — granița se validează în ambele capete, iar un câmp
 * redenumit pe server pică la `parse`, nu ajunge `undefined` în UI.
 */

/**
 * Modelul de factură al firmei.
 *
 * `layout` și `accent` sunt `string`, nu `enum`: listele închise trăiesc în modulul de
 * facturare, pe care `apps/crm` (`scope:fe`) n-are voie să-l importe, iar valorile valide
 * vin de la server prin `/invoice-model`. Ce se validează aici e **forma**, care e chiar
 * ce ar strica ecranul dacă serverul ar răspunde altfel.
 */
const invoiceThemeSchema = z.object({
  layout: z.string(),
  accent: z.string(),
  options: z.object({
    showLogo: z.boolean(),
    showBankDetails: z.boolean(),
    showProductCode: z.boolean(),
    showStamp: z.boolean(),
  }),
});

export type InvoiceTheme = z.infer<typeof invoiceThemeSchema>;

export const companySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  shortName: z.string().nullable(),
  cif: z.string(),
  // Serverul întoarce mereu un nume din listă (`normalizeCompanyIcon` la citire), deci
  // aici e `enum`, nu `string`: dacă vreodată n-ar fi, vrem să pice la `parse`.
  icon: z.enum(companyIconKeys),
  tradeRegisterNo: z.string().nullable(),
  vatPayer: z.boolean(),
  iban: z.string().nullable(),
  ibanEur: z.string().nullable(),
  treasuryIban: z.string().nullable(),
  bankName: z.string().nullable(),
  shareCapital: z.string().nullable(),
  invoiceContactName: z.string().nullable(),
  invoiceContactPhone: z.string().nullable(),
  invoiceContactEmail: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  billingAddress: z.string().nullable(),
  legalAddress: z.string().nullable(),
  city: z.string().nullable(),
  countyCode: z.string().nullable(),
  postalCode: z.string().nullable(),
  countryCode: z.string(),
  status: z.boolean(),
  /**
   * Cheia logo-ului de pe facturile firmei. `null` = n-a încărcat unul, deci factura ei
   * iese fără logo — nu cu al altei firme.
   *
   * ⚠️ **Nu e în `CompanyInput`.** Formularul trimite o înlocuire a firmei; logo-ul se
   * schimbă prin rutele lui, iar serverul nici nu-l citește din corpul salvării.
   */
  logoFile: z.string().nullable(),
  /**
   * Modelul după care ies facturile firmei.
   *
   * ⚠️ **Nu e în `CompanyInput`**, ca și logo-ul: formularul fișei trimite o înlocuire a
   * firmei, iar modelul se schimbă din ecranul lui. Serverul nici nu-l citește din corpul
   * salvării.
   */
  invoiceTheme: invoiceThemeSchema,
});

export type Company = z.infer<typeof companySchema>;

/** Ce trimite formularul. Validarea „de adevărat" (IBAN, CIF) e a serverului. */
/**
 * Denumirea cu care se arată firma **în interfață**.
 *
 * O funcție, nu un `??` repetat prin ecrane: căderea pe denumirea oficială e o regulă a
 * domeniului, iar scrisă într-un singur loc nu poate fi uitată la al treilea ecran.
 *
 * ⚠️ **Nu se folosește pentru documente.** Contractele și anexele tipăresc `name`, adică
 * denumirea din registrul comerțului — un contract semnat cu o poreclă nu e o problemă de
 * afișare. Tabelul consumatorilor:
 * `docs/wiki/domains/companies-and-clients.md#denumirea-scurtă`.
 */
export function companyDisplayName(company: { name: string; shortName: string | null }): string {
  return company.shortName ?? company.name;
}

export interface CompanyInput {
  readonly name: string;
  readonly shortName: string;
  readonly cif: string;
  readonly icon: CompanyIconKey;
  readonly tradeRegisterNo: string;
  readonly vatPayer: boolean;
  readonly iban: string;
  readonly ibanEur: string;
  readonly treasuryIban: string;
  readonly bankName: string;
  readonly shareCapital: string;
  readonly invoiceContactName: string;
  readonly invoiceContactPhone: string;
  readonly invoiceContactEmail: string;
  readonly phone: string;
  readonly email: string;
  /** Adresa veche, pe un rând. Formularul n-o mai editează, doar o trimite înapoi neatinsă. */
  readonly billingAddress: string;
  readonly legalAddress: string;
  readonly city: string;
  /** Cod din `RO_COUNTY_LIST`; șirul gol = necompletat. */
  readonly countyCode: string;
  readonly postalCode: string;
  readonly status: boolean;
}

/**
 * Firma are adresa structurată completă — stradă, localitate, județ (#183).
 *
 * Aceeași regulă ca `hasStructuredAddress` de pe server, scrisă din nou fiindcă `apps/crm`
 * n-are voie să importe `@pcrm/be/*`. E o condiție de trei câmpuri, nu o regulă de calcul.
 */
export function hasStructuredAddress(company: Company): boolean {
  return Boolean(company.legalAddress && company.city && company.countyCode);
}

export const COMPANIES_QUERY_KEY = ['companies'] as const;

/**
 * Lista de administrare: **și firmele dezactivate**.
 *
 * Altfel o firmă scoasă din uz ar dispărea fără urmă și n-ar mai putea fi reactivată.
 * Selectorul din sidebar cere aceeași listă și ascunde el ce e dezactivat — o a doua
 * cerere, doar cu cele active, ar fi două cache-uri care se pot contrazice după o editare.
 */
export const companiesQueryOptions = () =>
  queryOptions({
    queryKey: COMPANIES_QUERY_KEY,
    queryFn: () =>
      apiGet(
        '/companies?includeInactive=true',
        z.object({ companies: z.array(companySchema) }),
      ).then((body) => body.companies),
  });

export function createCompany(input: CompanyInput): Promise<Company> {
  return apiPost('/companies', input, z.object({ company: companySchema })).then(
    (body) => body.company,
  );
}

export function updateCompany(id: string, input: CompanyInput): Promise<Company> {
  return apiPatch(`/companies/${id}`, input, z.object({ company: companySchema })).then(
    (body) => body.company,
  );
}

/**
 * Încarcă sau înlocuiește logo-ul firmei.
 *
 * Cerere separată de salvarea fișei, trimisă pe loc: e un fișier, iar formularul n-are ce
 * face cu el până la `Salvează`.
 */
export function uploadCompanyLogo(input: { companyId: string; file: File }): Promise<Company> {
  return apiUpload(
    `/companies/${input.companyId}/logo`,
    input.file,
    z.object({ company: companySchema }),
  ).then((body) => body.company);
}

/**
 * Scoate logo-ul: de aici înainte firma tipărește fără.
 *
 * `DELETE`, nu o salvare cu câmp gol: absența e o stare pe care trebuie să poți ajunge
 * dinadins, nu una în care aluneci dintr-un formular.
 */
export function removeCompanyLogo(companyId: string): Promise<void> {
  return apiDelete(`/companies/${companyId}/logo`);
}

/**
 * Adresa logo-ului, pentru `<img src>`.
 *
 * `v=` e cheia din bază, care conține hash-ul conținutului: un logo înlocuit are altă
 * cheie, deci altă adresă, deci browserul chiar reîncarcă imaginea. Fără asta, răspunsul
 * e marcat `immutable` pe un an și previzualizarea ar rămâne pe cea veche.
 *
 * ⚠️ **Firma e în adresă.** Nu există „logo-ul firmei active": cine tipărește cere logo-ul
 * firmei actului.
 */
export function companyLogoUrl(companyId: string, logoFile: string): string {
  return `${apiFileUrl(`/companies/${companyId}/logo`)}?v=${encodeURIComponent(logoFile)}`;
}

const activeCompanySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  shortName: z.string().nullable(),
  icon: z.enum(companyIconKeys),
});

/**
 * `PUT`, nu `POST`: firma activă e o valoare care se **înlocuiește** pe sesiune, iar
 * trimiterea aceleiași firme de două ori trebuie să aibă același efect ca o dată.
 *
 * Nu există „golire": cât timp există măcar o firmă activă, sesiunea are una — serverul o
 * alege singur dacă operatorul n-a ales.
 */
export function setActiveCompany(companyId: string) {
  return apiPut(
    '/companies/active',
    { companyId },
    z.object({ activeCompany: activeCompanySchema }),
  ).then((body) => body.activeCompany);
}
